import { Injectable, NgZone } from '@angular/core';
import { Platform } from '@ionic/angular';

import { GoogleGeocode } from 'src/providers/google-geocode/google-geocode';

import { AutoCompleteAddressModel } from 'src/models/autocomplete-address.model';
import { Logger } from '../logger';

declare const google: any;

export interface AddressComponentsModel {
  city: string,
  state: string
}

export interface FullAddressComponentsModel {
  city: string;
  state: string;
  countryCode: string;
  countryName: string;
  postalCode: string;
}

@Injectable()
export class AddressForm {

  acService: any;
  autocompleteItems = [];
  autocompleteAddress: AutoCompleteAddressModel[];

  constructor(
    private googleGeocode: GoogleGeocode,
    private platform: Platform,
    public _zone: NgZone,
    private logger: Logger
  ) {
  }

  private async ensureGoogleMapsLoaded(): Promise<void> {
    if (typeof google !== 'undefined' && google.maps && google.maps.places && google.maps.places.AutocompleteService) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined' && google.maps && google.maps.places && google.maps.places.AutocompleteService) {
        resolve();
        return;
      }

      const checkInterval = setInterval(() => {
        if (typeof google !== 'undefined' && google.maps && google.maps.places && google.maps.places.AutocompleteService) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkInterval);
        if (typeof google === 'undefined' || !google.maps || !google.maps.places || !google.maps.places.AutocompleteService) {
          reject(new Error('Google Maps API failed to load'));
        }
      }, 10000);
    });
  }

  private async getAutocompleteService(): Promise<any> {
    await this.ensureGoogleMapsLoaded();
    if (!this.acService) {
      this.acService = new google.maps.places.AutocompleteService();
    }
    return this.acService;
  }

  async updateSearch(address: string, country = 'US') {
    if (address.length <= 1) {
      return [];
    }
    const config = {
      types: ['geocode'],
      input: address,
      componentRestrictions: { country }
    };

    const service = await this.getAutocompleteService();
    await service.getPlacePredictions(config,  (predictions, _status) => {
      if (predictions) {
        this._zone.run(() => {
          this.autocompleteItems = predictions;
        });
      } else {
        this.autocompleteItems = [];
      }
    });

    const autocompleteAddress = this.autocompleteItems.map(item => ({
        placeId: item.place_id,
        value: item.description,
        display: item.description
      }));
    return autocompleteAddress;
  }

  async updateSearchWorldwide(address: string) {
    if (address.length <= 1) {
      return [];
    }
    const config = {
      types: ['geocode'],
      input: address
    };
    const service = await this.getAutocompleteService();
    await service.getPlacePredictions(config, (predictions, _status) => {
      if (predictions) {
        this._zone.run(() => {
          this.autocompleteItems = predictions;
        });
      } else {
        this.autocompleteItems = [];
      }
    });
    const autocompleteAddress = this.autocompleteItems.map(item => ({
      placeId: item.place_id,
      value: item.description,
      display: item.description
    }));
    return autocompleteAddress;
  }

  chooseItem = async (item) => {
    const response: any = await this.googleGeocode.getGeocode(item.placeId);
    const address_components = response.results[0].address_components;
    const zipcode = this.findPostalCode(address_components);
    return zipcode;
  }

  async getAdditonalAddressInfo(placeId) {
    try {
      const response: any = await this.googleGeocode.getGeocode(placeId);
      const addressComponents = response.results[0].address_components;

      let addressList = { state: '', city: '' };

      addressComponents.map((address) => {
        if (address.types.includes('locality') || address.types.includes('postal_town') || address.types.includes('sublocality')) {
          addressList = { ...addressList, city: address.long_name };
        }

        if (address.types.includes('administrative_area_level_1')) {
          addressList = { ...addressList, state: address.long_name };
        }
      });

      return addressList;
    } catch (err) {
      this.logger.error(err, 'error getting address additional data');
      return { state: '', city: '' };
    }
  }

  async getFullAddressComponents(placeId: string): Promise<FullAddressComponentsModel> {
    try {
      const response: any = await this.googleGeocode.getGeocode(placeId);
      const addressComponents = response.results[0].address_components;
      return {
        city: this.findComponent(addressComponents, ['locality', 'postal_town', 'sublocality']),
        state: this.findComponent(addressComponents, ['administrative_area_level_1']),
        countryCode: this.findComponent(addressComponents, ['country'], 'short_name'),
        countryName: this.findComponent(addressComponents, ['country'], 'long_name'),
        postalCode: this.findPostalCode(addressComponents)
      };
    } catch (err) {
      this.logger.error(err, 'error getting full address components');
      return { city: '', state: '', countryCode: 'US', countryName: 'United States', postalCode: '' };
    }
  }

  private findComponent(components: any[], types: string[], nameType = 'long_name'): string {
    for (const type of types) {
      const component = components.find(c => c.types.includes(type));
      if (component) {
        return component[nameType];
      }
    }
    return '';
  }

  findPostalCode(addressComponents) {
    let postalCode = '';
    if (addressComponents) {
      for (const i in addressComponents) {
        if (addressComponents[i].types[0] === 'postal_code') {
          postalCode = addressComponents[i].long_name;
        }
      }
    } else {
      postalCode = null;
    }
    return postalCode;
  }

  addressError(formValue) {
    let hasError = false;
    const address = formValue.toLowerCase();
    const errors = [
      'po box',
      'p.o. box',
      'p o box',
      'p. o box',
      'p o. box',
      'p.o box',
    ];
    errors.map((error) => {
      if (address.includes(error)) {
        return hasError = true;
      }
    })
    return hasError;
  }
}
